import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { fetchLedger } from "../../../redux/slices/accounting";
import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAuth from "../../../hooks/useAuth";
import { KeyboardArrowUp, KeyboardArrowDown, Print } from "@mui/icons-material";
import { format } from "date-fns";
import { Button } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fi } from "date-fns/locale";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const PrintableTable = styled(Table)`
  @media print {
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 10px !important;
      padding: 4px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
    & .expand-button {
      display: none !important;
    }
  }
`;

const PrintableContainer = styled("div")`
  @media print {
    & .no-print {
      display: none !important;
    }
    & .MuiBreadcrumbs-root {
      display: none !important;
    }
    & .MuiDivider-root {
      display: none !important;
    }
  }
`;

function Ledger(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { activeFiscalYear, ledger, status } = useAppSelector(
    (state) => state.accounting
  );

  const [expandedAccounts, setExpandedAccounts] = useState<any>({});
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const toggleExpand = (accountCode: any) => {
    setExpandedAccounts((prev: any) => ({
      ...prev,
      [accountCode]: !prev[accountCode],
    }));
  };

  const handlePrint = () => {
    const allExpanded = ledger.reduce((acc: any, account: any) => {
      acc[account.accountCode] = true;
      return acc;
    }, {});
    setExpandedAccounts(allExpanded);

    setTimeout(() => {
      window.print();
    }, 100);
  };

  const runSearch = () => {
    if (!from || !to) return;
    dispatch(
      fetchLedger({
        associationId,
        from: format(from, "yyyy-MM-dd"),
        to: format(to, "yyyy-MM-dd"),
      })
    );
  };

  useEffect(() => {
    if (activeFiscalYear) {
      setFrom(new Date(activeFiscalYear.from));
      setTo(new Date(activeFiscalYear.to));
    }
  }, [activeFiscalYear]);

  useEffect(() => {
    if (!currentUser || !activeFiscalYear) return;
    dispatch(
      fetchLedger({
        associationId,
        from: format(new Date(activeFiscalYear.from), "yyyy-MM-dd"),
        to: format(new Date(activeFiscalYear.to), "yyyy-MM-dd"),
      })
    );
  }, [dispatch, currentUser, activeFiscalYear, associationId]);

  useEffect(() => {
    if (ledger && ledger.length > 0) {
      const expandedState = ledger.reduce((acc: any, account: any) => {
        acc[account.accountCode] = true;
        return acc;
      }, {});
      setExpandedAccounts(expandedState);
    }
  }, [ledger]);

  return (
    <PrintableContainer>
      <Helmet title="Blank" />
      <Typography
        variant="h3"
        gutterBottom
        display="inline"
        className="no-print"
      >
        Pääkirja
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Pääkirja</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card mb={6}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Pääkirja
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={fi}
                >
                  <Box
                    className="no-print"
                    display="flex"
                    gap={2}
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                  >
                    <DatePicker
                      label="Alkaen"
                      inputFormat="dd.MM.yyyy"
                      value={from}
                      onChange={(v) => setFrom(v)}
                      maxDate={to ?? undefined}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                    <DatePicker
                      label="Päättyy"
                      inputFormat="dd.MM.yyyy"
                      value={to}
                      onChange={(v) => setTo(v)}
                      minDate={from ?? undefined}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                    <Button variant="contained" onClick={runSearch}>
                      Hae
                    </Button>
                  </Box>
                </LocalizationProvider>
                <IconButton
                  onClick={handlePrint}
                  title="Tulosta"
                  className="no-print"
                >
                  <Print />
                </IconButton>
              </Box>

              <PrintableTable size={"small"}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tili</TableCell>
                    <TableCell>Kuvaus</TableCell>
                    <TableCell align="right">Debet (€)</TableCell>
                    <TableCell align="right">Kredit (€)</TableCell>
                    <TableCell>Pvm</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {status === "loading" ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="50vh"
                        >
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : ledger.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Ei tilejä
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledger.map((account: any) => (
                      <React.Fragment key={account.accountCode}>
                        {/* Toggle Row */}
                        <TableRow>
                          <TableCell colSpan={5}>
                            <IconButton
                              onClick={() => toggleExpand(account.accountCode)}
                              sx={{ verticalAlign: "middle" }}
                              className="expand-button"
                            >
                              {expandedAccounts[account.accountCode] ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                display: "inline",
                                ml: 1,
                              }}
                            >
                              {account.account.name} - {account.account.code}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Transaction Rows */}
                        {expandedAccounts[account.accountCode] &&
                          account.transactions.map((transaction: any) => (
                            <TableRow key={transaction.id}>
                              <TableCell>{account.accountCode}</TableCell>
                              <TableCell>{transaction.description}</TableCell>
                              <TableCell align="right">
                                {new Intl.NumberFormat("fi-FI", {
                                  style: "currency",
                                  currency: "EUR",
                                }).format(transaction.debit)}
                              </TableCell>
                              <TableCell align="right">
                                {new Intl.NumberFormat("fi-FI", {
                                  style: "currency",
                                  currency: "EUR",
                                }).format(transaction.credit)}
                              </TableCell>
                              <TableCell>
                                {format(
                                  new Date(transaction.date),
                                  "dd.MM.yyyy HH:mm"
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </PrintableTable>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PrintableContainer>
  );
}

export default Ledger;
